import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Download, Eye, Edit, Trash2, FileText, 
  Calendar, Building, DollarSign, Hash, User, Filter, CheckCircle, XCircle, Send
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/DataTable/DataTable';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import Breadcrumb from '../../components/Common/Breadcrumb';
import { toast } from 'react-toastify';
import salesQuotationService from '../../services/salesQuotationService';

const SalesQuotation = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelItem, setCancelItem] = useState(null);
  const [stats, setStats] = useState({
    totalQuotations: 0,
    draftQuotations: 0,
    sentQuotations: 0,
    approvedQuotations: 0,
    acceptedQuotations: 0,
    rejectedQuotations: 0,
    totalValue: 0,
    totalTaxAmount: 0,
    totalAmountWithTax: 0
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });

  // Load data on component mount
  useEffect(() => {
    loadData();
    loadStats();
  }, []);

  // Dummy data for Sales Quotation (fallback)
  const dummyQuotations = [
    {
      id: '1',
      quotationNumber: 'SQ-2024-001',
      quotationDate: '2024-01-15',
      customerName: 'TechCorp Solutions',
      customerCode: 'CUST-001',
      currency: 'USD',
      totalAmountWithTax: 15000.00,
      totalTaxAmount: 1500.00,
      status: 'Draft',
      validUntil: '2024-02-15',
      createdBy: { firstName: 'John', lastName: 'Smith' },
      createdAt: '2024-01-15T10:30:00Z'
    },
    {
      id: '2',
      quotationNumber: 'SQ-2024-002',
      quotationDate: '2024-01-16',
      customerName: 'Global Manufacturing Inc',
      customerCode: 'CUST-002',
      currency: 'USD',
      totalAmountWithTax: 25000.00,
      totalTaxAmount: 2500.00,
      status: 'Sent',
      validUntil: '2024-02-16',
      createdBy: { firstName: 'Maria', lastName: 'Garcia' },
      createdAt: '2024-01-16T14:20:00Z'
    }
  ];

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await salesQuotationService.getAllSalesQuotations({
        page: pagination.current,
        limit: 10,
        search: searchTerm
      });

      if (response.success) {
        // Transform _id to id for consistency
        const transformedData = (response.data || []).map(quotation => ({
          ...quotation,
          id: quotation._id || quotation.id
        }));
        setData(transformedData);
        setPagination(response.pagination);
      } else {
        toast.error('Failed to load sales quotations');
        // Use dummy data as fallback
        setData(dummyQuotations);
      }
    } catch (error) {
      console.error('Error loading sales quotations:', error);
      toast.error('Failed to load sales quotations');
      // Use dummy data as fallback
      setData(dummyQuotations);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await salesQuotationService.getSalesQuotationStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Search functionality
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    loadData();
  };

  // Pagination
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
    loadData();
  };

  // Filter data based on search term (for client-side filtering if needed)
  const filteredData = data.filter(item =>
    item.quotationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.customerCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Table columns configuration
  const columns = [
    {
      key: 'quotationNumber',
      label: 'Quotation #',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-blue-600" />
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      key: 'quotationDate',
      label: 'Quotation Date',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-gray-500" />
          <span>{new Date(value).toLocaleDateString()}</span>
        </div>
      )
    },
    {
      key: 'customerName',
      label: 'Customer Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{row.customerCode}</div>
        </div>
      )
    },
    {
      key: 'currency',
      label: 'Currency',
      sortable: true,
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value}
        </span>
      )
    },
    {
      key: 'totalAmountWithTax',
      label: 'Total Amount',
      sortable: true,
      render: (value, row) => (
        <div className="text-right">
          <div className="font-medium text-gray-900">
            {row.currency} {value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
          <div className="text-sm text-gray-500">
            Tax: {row.currency} {row.totalTaxAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusConfig = {
          'Draft': { color: 'bg-gray-100 text-gray-800', icon: '📝' },
          'Sent': { color: 'bg-blue-100 text-blue-800', icon: '📤' },
          'Under Review': { color: 'bg-yellow-100 text-yellow-800', icon: '👀' },
          'Negotiation': { color: 'bg-purple-100 text-purple-800', icon: '💬' },
          'Approved': { color: 'bg-green-100 text-green-800', icon: '✅' },
          'Accepted': { color: 'bg-green-100 text-green-800', icon: '✓' },
          'Rejected': { color: 'bg-red-100 text-red-800', icon: '❌' },
          'Expired': { color: 'bg-orange-100 text-orange-800', icon: '⏰' },
          'Withdrawn': { color: 'bg-gray-100 text-gray-800', icon: '↩️' }
        };
        const config = statusConfig[value] || statusConfig['Draft'];
        
        return (
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
            <span>{config.icon}</span>
            {value}
          </span>
        );
      }
    },
    {
      key: 'validUntil',
      label: 'Valid Until',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-gray-500" />
          <span>{value ? new Date(value).toLocaleDateString() : '-'}</span>
        </div>
      )
    }
  ];

  // Event handlers
  const handleAddNew = () => {
    navigate('/sales/sales-quotation/new');
  };

  const handleView = (item) => {
    navigate(`/sales/sales-quotation/view/${item.id}`);
  };

  const handleEdit = (item) => {
    navigate(`/sales/sales-quotation/edit/${item.id}`);
  };

  const handleApprove = (item) => {
    // Navigate to approve page (read-only edit mode)
    navigate(`/sales/sales-quotation/approve/${item.id}`);
  };

  const handleSend = (item) => {
    // Navigate to send page (read-only edit mode)
    navigate(`/sales/sales-quotation/send/${item.id}`);
  };

  const handleCancel = (item) => {
    setCancelItem(item);
    setShowCancelModal(true);
  };

  const confirmCancel = async () => {
    if (!cancelItem) return;

    try {
      setLoading(true);
      const response = await salesQuotationService.updateSalesQuotation(cancelItem.id, {
        status: 'Withdrawn'
      });

      if (response.success) {
        toast.success('Sales quotation withdrawn successfully');
        setShowCancelModal(false);
        setCancelItem(null);
        loadData(); // Reload the list
        loadStats(); // Reload statistics
      } else {
        toast.error(response.message || 'Failed to withdraw sales quotation');
      }
    } catch (error) {
      console.error('Error withdrawing sales quotation:', error);
      toast.error('Failed to withdraw sales quotation');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (item) => {
    setDeleteItem(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;

    try {
      setLoading(true);
      // Use _id if available, otherwise fall back to id
      const quotationId = deleteItem._id || deleteItem.id;
      if (!quotationId) {
        toast.error('Invalid quotation ID');
        return;
      }
      
      console.log('Deleting quotation with ID:', quotationId);
      // Call the API to delete the quotation
      const response = await salesQuotationService.deleteSalesQuotation(quotationId);
      
      console.log('Delete response:', response);
      
      if (response.success) {
        toast.success('Sales quotation deleted successfully');
        setShowDeleteModal(false);
        setDeleteItem(null);
        // Reload data and stats after successful deletion
        loadData();
        loadStats();
      } else {
        toast.error(response.message || 'Failed to delete sales quotation');
      }
    } catch (error) {
      console.error('Error deleting sales quotation:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete sales quotation';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteItem(null);
  };

  const handleExport = () => {
    // Export functionality
    console.log('Exporting sales quotations...');
    toast.success('Sales quotations exported successfully');
  };

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Transactions',
      path: '/sales?tab=transactions',
      onClick: () => navigate('/sales?tab=transactions')
    },
    {
      label: 'Sales Quotation',
      path: '/sales/sales-quotation',
      isCurrent: true
    }
  ];

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Sales Quotation</h1>
          <p>Create and manage sales quotations for prospects</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleAddNew}>
            <Plus size={18} />
            Create Quotation
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <FileText size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalQuotations}</div>
          <div className="metric-title">Total Quotations</div>
          <div className="metric-subtitle">All sales quotations</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Calendar size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Draft</span>
            </div>
          </div>
          <div className="metric-value">{stats.draftQuotations}</div>
          <div className="metric-title">Draft Quotations</div>
          <div className="metric-subtitle">In preparation</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Send size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Sent</span>
            </div>
          </div>
          <div className="metric-value">{stats.sentQuotations || 0}</div>
          <div className="metric-title">Sent Quotations</div>
          <div className="metric-subtitle">Sent to customers</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Approved</span>
            </div>
          </div>
          <div className="metric-value">{stats.approvedQuotations || 0}</div>
          <div className="metric-title">Approved Quotations</div>
          <div className="metric-subtitle">Ready for sending</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Accepted</span>
            </div>
          </div>
          <div className="metric-value">{stats.acceptedQuotations || 0}</div>
          <div className="metric-title">Accepted Quotations</div>
          <div className="metric-subtitle">Customer accepted</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <XCircle size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Rejected</span>
            </div>
          </div>
          <div className="metric-value">{stats.rejectedQuotations || 0}</div>
          <div className="metric-title">Rejected Quotations</div>
          <div className="metric-subtitle">Customer rejected</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Building size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">${stats.totalAmountWithTax.toLocaleString('en-US', { minimumFractionDigits: 0 })}</div>
          <div className="metric-title">Total Amount</div>
          <div className="metric-subtitle">All quotations combined</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Subtotal</span>
            </div>
          </div>
          <div className="metric-value">${(stats.totalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}</div>
          <div className="metric-title">Quotation Amount</div>
          <div className="metric-subtitle">Total without tax</div>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={filteredData}
        columns={columns}
        loading={loading}
        onView={handleView}
        onEdit={handleEdit}
        onApprove={(row) => {
          // Only show approve action for Draft quotations
          if (row.status === 'Draft') {
            handleApprove(row);
          }
        }}
        onCancel={(row) => {
          // Only show cancel action for Draft or Sent quotations
          if (row.status === 'Draft' || row.status === 'Sent') {
            handleCancel(row);
          }
        }}
        onDelete={handleDelete}
        onExport={handleExport}
        noCard={true}
        className="sales-quotation-table"
        pageSize={10}
      />

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteItem(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Sales Quotation"
        message={`Are you sure you want to delete quotation ${deleteItem?.quotationNumber}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <ConfirmationModal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setCancelItem(null);
        }}
        onConfirm={confirmCancel}
        title="Withdraw Sales Quotation"
        message={`Are you sure you want to withdraw quotation ${cancelItem?.quotationNumber}? This action cannot be undone.`}
        confirmText="Withdraw Quotation"
        cancelText="No, Keep It"
        type="warning"
      />
    </div>
  );
};

export default SalesQuotation;

